import { useEffect, useState } from 'react';
import {
  useCrearCategoriaProducto,
  useCrearProducto,
  useCrearUnidadMedida,
} from '../api/queries-finanzas';
import type { CategoriaProducto, UnidadMedida } from '../api/types';
import { BoxIcon, CheckIcon, PlusIcon, SparkIcon } from './icons';
import { Modal } from './Modal';
import { SearchSelect } from './SearchSelect';
import { useToast } from '../ui/ToastProvider';
import { useCurrency } from '../currency/CurrencyProvider';

type TipoProducto = 'ARTICULO' | 'SERVICIO';

interface ProductoForm {
  sku: string;
  nombre: string;
  categoria: string;
  unidad: string;
  tipo: TipoProducto;
  precioUsd: string;
  impuestoPct: string;
  stockActual: string;
  stockMinimo: string;
  costoPromedioUsd: string;
  stockRuta: string;
  controlaInventario: boolean;
}

interface ProductoModalProps {
  categorias: CategoriaProducto[];
  unidades: UnidadMedida[];
  onClose: () => void;
  onCreated: (nombre: string) => void;
}

export function ProductoModal({ categorias, unidades, onClose, onCreated }: ProductoModalProps) {
  const { notify } = useToast();
  const { usdRate } = useCurrency();
  const crearProducto = useCrearProducto();
  const crearCategoria = useCrearCategoriaProducto();
  const crearUnidad = useCrearUnidadMedida();

  const categoriasActivas = categorias.filter((categoria) => categoria.activo);
  const unidadesActivas = unidades.filter((unidad) => unidad.activo);
  const unidadServicio = unidadesActivas.find((unidad) => unidad.codigo === 'SERVICIO');
  const unidadBase = unidadesActivas.find((unidad) => unidad.codigo === 'UND') ?? unidadesActivas[0];

  const [form, setForm] = useState<ProductoForm>({
    sku: '',
    nombre: '',
    categoria:
      categoriasActivas.find((categoria) => categoria.nombre.toLowerCase() === 'general')?.nombre ??
      categoriasActivas[0]?.nombre ??
      '',
    unidad: unidadBase?.codigo ?? 'UND',
    tipo: 'ARTICULO',
    precioUsd: '0',
    impuestoPct: '16',
    stockActual: '0',
    stockMinimo: '0',
    costoPromedioUsd: '0',
    stockRuta: '0',
    controlaInventario: true,
  });

  const [nuevaCategoria, setNuevaCategoria] = useState(false);
  const [categoriaDraft, setCategoriaDraft] = useState({ nombre: '', descripcion: '' });
  const [nuevaUnidad, setNuevaUnidad] = useState(false);
  const [unidadDraft, setUnidadDraft] = useState({ codigo: '', nombre: '', permiteDecimales: false });

  const esServicio = form.tipo === 'SERVICIO';

  // El catálogo puede llegar después de abrir el modal; completa los valores
  // iniciales sin pisar lo que el usuario ya haya elegido.
  useEffect(() => {
    if (categoriasActivas.length === 0) return;
    setForm((prev) =>
      prev.categoria
        ? prev
        : {
            ...prev,
            categoria:
              categoriasActivas.find(
                (categoria) => categoria.nombre.toLowerCase() === 'general',
              )?.nombre ?? categoriasActivas[0]?.nombre ?? '',
          },
    );
  }, [categoriasActivas]);

  useEffect(() => {
    if (unidadesActivas.length === 0) return;
    setForm((prev) =>
      prev.unidad ? prev : { ...prev, unidad: unidadesActivas[0]?.codigo ?? 'UND' },
    );
  }, [unidadesActivas]);

  const cambiarTipo = (tipo: TipoProducto) => {
    setForm((prev) => ({
      ...prev,
      tipo,
      unidad:
        tipo === 'SERVICIO'
          ? (unidadServicio?.codigo ?? prev.unidad)
          : prev.unidad === 'SERVICIO'
            ? (unidadBase?.codigo ?? 'UND')
            : prev.unidad,
      controlaInventario: tipo === 'SERVICIO' ? false : true,
    }));
  };

  const submitCategoria = async () => {
    if (categoriaDraft.nombre.trim().length < 2) {
      notify('Escribe un nombre de categoría de al menos 2 caracteres.', 'warning');
      return;
    }
    try {
      const response = await crearCategoria.mutateAsync({
        nombre: categoriaDraft.nombre.trim(),
        ...(categoriaDraft.descripcion.trim() ? { descripcion: categoriaDraft.descripcion.trim() } : {}),
      });
      setForm((prev) => ({ ...prev, categoria: response.data.nombre }));
      setCategoriaDraft({ nombre: '', descripcion: '' });
      setNuevaCategoria(false);
      notify(`Categoría «${response.data.nombre}» creada.`, 'success');
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo crear la categoría.', 'error');
    }
  };

  const submitUnidad = async () => {
    if (!/^[A-Za-z0-9._-]{1,10}$/.test(unidadDraft.codigo.trim())) {
      notify('El código de la unidad debe tener entre 1 y 10 caracteres válidos.', 'warning');
      return;
    }
    if (unidadDraft.nombre.trim().length < 2) {
      notify('Escribe el nombre de la unidad.', 'warning');
      return;
    }
    try {
      const response = await crearUnidad.mutateAsync({
        codigo: unidadDraft.codigo.trim().toUpperCase(),
        nombre: unidadDraft.nombre.trim(),
        permiteDecimales: unidadDraft.permiteDecimales,
      });
      setForm((prev) => ({ ...prev, unidad: response.data.codigo }));
      setUnidadDraft({ codigo: '', nombre: '', permiteDecimales: false });
      setNuevaUnidad(false);
      notify(`Unidad «${response.data.codigo}» creada.`, 'success');
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo crear la unidad.', 'error');
    }
  };

  const submitProducto = async () => {
    if (form.sku.trim().length < 2 || form.nombre.trim().length < 2) {
      notify('SKU y nombre son obligatorios.', 'warning');
      return;
    }
    if (!form.categoria || !form.unidad) {
      notify('Selecciona o crea una categoría y una unidad de medida.', 'warning');
      return;
    }
    try {
      await crearProducto.mutateAsync({
        sku: form.sku.trim().toUpperCase(),
        nombre: form.nombre.trim(),
        categoria: form.categoria,
        unidad: form.unidad,
        tipo: form.tipo,
        precioUsd: (Number(form.precioUsd) || 0) / usdRate,
        impuestoPct: Number(form.impuestoPct) || 0,
        stockActual: esServicio ? 0 : Number(form.stockActual) || 0,
        stockMinimo: esServicio ? 0 : Number(form.stockMinimo) || 0,
        costoPromedioUsd: esServicio ? 0 : (Number(form.costoPromedioUsd) || 0) / usdRate,
        stockRuta: esServicio ? 0 : Number(form.stockRuta) || 0,
        controlaInventario: esServicio ? false : form.controlaInventario,
      });
      onCreated(form.nombre.trim());
      onClose();
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo crear el producto.', 'error');
    }
  };

  const categoriaVisible =
    form.categoria && !categoriasActivas.some((categoria) => categoria.nombre === form.categoria);
  const unidadVisible =
    form.unidad && !unidadesActivas.some((unidad) => unidad.codigo === form.unidad);

  return (
    <Modal
      title="Nuevo producto o servicio"
      eyebrow="Catálogo"
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn btn--outline" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void submitProducto()}
            disabled={crearProducto.isPending}
          >
            {crearProducto.isPending
              ? 'Guardando…'
              : esServicio
                ? 'Crear servicio'
                : 'Crear artículo'}
          </button>
        </>
      }
    >
      <div className="modal-card__body">
        <div className="type-picker" role="radiogroup" aria-label="Tipo de producto">
          <button
            type="button"
            role="radio"
            aria-checked={form.tipo === 'ARTICULO'}
            className={`type-option${form.tipo === 'ARTICULO' ? ' is-active' : ''}`}
            onClick={() => cambiarTipo('ARTICULO')}
          >
            <span className="type-option__icon">
              <BoxIcon size={21} />
            </span>
            <span className="type-option__copy">
              <strong>Artículo</strong>
              <small>Controla stock, kardex y costo promedio.</small>
            </span>
            {form.tipo === 'ARTICULO' ? <CheckIcon size={17} /> : null}
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={form.tipo === 'SERVICIO'}
            className={`type-option${form.tipo === 'SERVICIO' ? ' is-active' : ''}`}
            onClick={() => cambiarTipo('SERVICIO')}
          >
            <span className="type-option__icon">
              <SparkIcon size={21} />
            </span>
            <span className="type-option__copy">
              <strong>Servicio</strong>
              <small>Se factura sin inventario ni kardex.</small>
            </span>
            {form.tipo === 'SERVICIO' ? <CheckIcon size={17} /> : null}
          </button>
        </div>

        <section className="form-section">
          <div className="form-section__head">
            <div>
              <h3 className="form-section__title">Datos generales</h3>
              <p className="form-section__hint">
                Identifica el producto y su clasificación operativa.
              </p>
            </div>
          </div>
          <div className="form-grid form-grid--3">
            <label className="field">
              <span className="field-label">SKU *</span>
              <input
                className="input"
                value={form.sku}
                onChange={(event) =>
                  setForm({ ...form, sku: event.target.value.toUpperCase() })
                }
                placeholder="FERT-001"
              />
            </label>
            <label className="field field--wide">
              <span className="field-label">Nombre *</span>
              <input
                className="input"
                value={form.nombre}
                onChange={(event) => setForm({ ...form, nombre: event.target.value })}
                placeholder={esServicio ? 'Análisis de suelo' : 'Fertilizante 15-15-15'}
              />
            </label>
            <div className="field">
              <span className="field-label">Categoría *</span>
              <div className="input-with-action">
                <SearchSelect
                  value={form.categoria}
                  onChange={(value) => setForm({ ...form, categoria: value })}
                  options={[
                    ...(categoriasActivas.length === 0 && !categoriaVisible
                      ? [{ value: '', label: 'Sin categorías activas' }]
                      : []),
                    ...(categoriaVisible && form.categoria
                      ? [{ value: form.categoria, label: form.categoria }]
                      : []),
                    ...categoriasActivas.map((categoria) => ({
                      value: categoria.nombre,
                      label: categoria.nombre,
                    })),
                  ]}
                  placeholder="Buscar categoría"
                  ariaLabel="Categoría"
                />
                <button
                  type="button"
                  className="btn btn--outline btn--icon"
                  aria-label="Crear categoría"
                  title="Crear categoría"
                  onClick={() => setNuevaCategoria((value) => !value)}
                >
                  <PlusIcon size={16} />
                </button>
              </div>
            </div>
            <div className="field">
              <span className="field-label">Unidad de medida *</span>
              <div className="input-with-action">
                <SearchSelect
                  value={form.unidad}
                  onChange={(value) => setForm({ ...form, unidad: value })}
                  options={[
                    ...(unidadesActivas.length === 0 && !unidadVisible
                      ? [{ value: '', label: 'Sin unidades activas' }]
                      : []),
                    ...(unidadVisible && form.unidad
                      ? [{ value: form.unidad, label: form.unidad }]
                      : []),
                    ...unidadesActivas.map((unidad) => ({
                      value: unidad.codigo,
                      label: `${unidad.codigo} · ${unidad.nombre}`,
                    })),
                  ]}
                  placeholder="Buscar unidad"
                  ariaLabel="Unidad de medida"
                />
                <button
                  type="button"
                  className="btn btn--outline btn--icon"
                  aria-label="Crear unidad de medida"
                  title="Crear unidad de medida"
                  onClick={() => setNuevaUnidad((value) => !value)}
                >
                  <PlusIcon size={16} />
                </button>
              </div>
            </div>
            <label className="field">
              <span className="field-label">IVA %</span>
              <select
                className="input"
                value={form.impuestoPct}
                onChange={(event) => setForm({ ...form, impuestoPct: event.target.value })}
              >
                <option value="0">Exento · 0%</option>
                <option value="8">8%</option>
                <option value="16">General · 16%</option>
                <option value="31">31%</option>
              </select>
            </label>
          </div>

          {nuevaCategoria ? (
            <div className="quick-create">
              <div className="quick-create__head">
                <strong>Nueva categoría</strong>
                <span>Quedará disponible para todo el catálogo.</span>
              </div>
              <div className="form-grid">
                <label className="field">
                  <span className="field-label">Nombre *</span>
                  <input
                    className="input"
                    value={categoriaDraft.nombre}
                    onChange={(event) =>
                      setCategoriaDraft({ ...categoriaDraft, nombre: event.target.value })
                    }
                    placeholder="Ej. Nutrición foliar"
                  />
                </label>
                <label className="field">
                  <span className="field-label">Descripción</span>
                  <input
                    className="input"
                    value={categoriaDraft.descripcion}
                    onChange={(event) =>
                      setCategoriaDraft({ ...categoriaDraft, descripcion: event.target.value })
                    }
                    placeholder="Opcional"
                  />
                </label>
              </div>
              <div className="quick-create__actions">
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => setNuevaCategoria(false)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  onClick={() => void submitCategoria()}
                  disabled={crearCategoria.isPending}
                >
                  {crearCategoria.isPending ? 'Creando…' : 'Guardar categoría'}
                </button>
              </div>
            </div>
          ) : null}

          {nuevaUnidad ? (
            <div className="quick-create">
              <div className="quick-create__head">
                <strong>Nueva unidad de medida</strong>
                <span>El código se usa en documentos e inventario.</span>
              </div>
              <div className="form-grid form-grid--3">
                <label className="field">
                  <span className="field-label">Código *</span>
                  <input
                    className="input"
                    value={unidadDraft.codigo}
                    onChange={(event) =>
                      setUnidadDraft({ ...unidadDraft, codigo: event.target.value.toUpperCase() })
                    }
                    placeholder="BULTO"
                    maxLength={10}
                  />
                </label>
                <label className="field">
                  <span className="field-label">Nombre *</span>
                  <input
                    className="input"
                    value={unidadDraft.nombre}
                    onChange={(event) =>
                      setUnidadDraft({ ...unidadDraft, nombre: event.target.value })
                    }
                    placeholder="Bulto de 25 kg"
                  />
                </label>
                <label className="checkbox checkbox--compact">
                  <input
                    type="checkbox"
                    checked={unidadDraft.permiteDecimales}
                    onChange={(event) =>
                      setUnidadDraft({ ...unidadDraft, permiteDecimales: event.target.checked })
                    }
                  />
                  Permite decimales
                </label>
              </div>
              <div className="quick-create__actions">
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => setNuevaUnidad(false)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  onClick={() => void submitUnidad()}
                  disabled={crearUnidad.isPending}
                >
                  {crearUnidad.isPending ? 'Creando…' : 'Guardar unidad'}
                </button>
              </div>
            </div>
          ) : null}
        </section>

        <section className="form-section">
          <div className="form-section__head">
            <div>
              <h3 className="form-section__title">Precio y facturación</h3>
              <p className="form-section__hint">El precio base se expresa en bolívares (Bs.).</p>
            </div>
          </div>
          <div className="form-grid">
            <label className="field">
              <span className="field-label">
                {esServicio ? 'Precio del servicio Bs.' : 'Precio de venta Bs.'}
              </span>
              <input
                className="input"
                type="number"
                min="0"
                step="0.01"
                value={form.precioUsd}
                onChange={(event) => setForm({ ...form, precioUsd: event.target.value })}
              />
            </label>
            <div className="field">
              <span className="field-label">Unidad aplicada</span>
              <p className="field-static">
                {form.unidad || '—'} ·{' '}
                {unidadesActivas.find((unidad) => unidad.codigo === form.unidad)?.nombre ??
                  'Unidad personalizada'}
              </p>
            </div>
          </div>
        </section>

        {esServicio ? (
          <section className="form-section">
            <div className="inline-note">
              <strong>Servicio sin inventario.</strong> No genera kardex, stock mínimo ni costo
              promedio. Se factura directo a la cuenta de ingreso y puede repetirse en cada venta.
            </div>
          </section>
        ) : (
          <section className="form-section">
            <div className="form-section__head">
              <div>
                <h3 className="form-section__title">Inventario inicial</h3>
                <p className="form-section__hint">
                  Opcional. Puedes registrar entradas después desde Compras o Ajustes.
                </p>
              </div>
            </div>
            <div className="form-grid form-grid--3">
              <label className="field">
                <span className="field-label">Stock actual</span>
                <input
                  className="input"
                  type="number"
                  min="0"
                  value={form.stockActual}
                  onChange={(event) => setForm({ ...form, stockActual: event.target.value })}
                  disabled={!form.controlaInventario}
                />
              </label>
              <label className="field">
                <span className="field-label">Stock mínimo</span>
                <input
                  className="input"
                  type="number"
                  min="0"
                  value={form.stockMinimo}
                  onChange={(event) => setForm({ ...form, stockMinimo: event.target.value })}
                  disabled={!form.controlaInventario}
                />
              </label>
              <label className="field">
                <span className="field-label">Costo promedio Bs.</span>
                <input
                  className="input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.costoPromedioUsd}
                  onChange={(event) =>
                    setForm({ ...form, costoPromedioUsd: event.target.value })
                  }
                  disabled={!form.controlaInventario}
                />
              </label>
              <label className="field">
                <span className="field-label">Stock en ruta</span>
                <input
                  className="input"
                  type="number"
                  min="0"
                  value={form.stockRuta}
                  onChange={(event) => setForm({ ...form, stockRuta: event.target.value })}
                  disabled={!form.controlaInventario}
                />
              </label>
              <label className="checkbox field--wide">
                <input
                  type="checkbox"
                  checked={form.controlaInventario}
                  onChange={(event) =>
                    setForm({ ...form, controlaInventario: event.target.checked })
                  }
                />
                Controla inventario (kardex y costo promedio)
              </label>
            </div>
          </section>
        )}
      </div>
    </Modal>
  );
}
